'use client'

import { Card } from '@repo/web-ui/components/card'
import { useLocale, useTranslations } from 'next-intl'
import * as React from 'react'
import { Typography } from '@repo/web-ui/components/typography'
import { CheckCircleInvertedIcon } from 'primitives/icons'
import { sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

import { FreeTrialIllustration } from './illustration/free-trial-illustration'
import { Button } from '@repo/design-system-cf/components/button'
import { myAccountTrackingEvents } from '../tracking/my-account-tracking-events'
import { useFeatureIsOn } from 'growthbook'
import { useUserIsEligibleForAllAccessYearlyExtensionQuery } from '@repo/cf-user/hooks/use-user-data'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import { OneClickYearlyExtensionButton } from 'payments/one-click-yearly-extension-button/components/one-click-yearly-extension-button'
import type { Language } from 'codegen/types'

export const YearlyExtensionBanner = ({
  showBanner,
  featureFlag,
}: {
  showBanner?: boolean
  featureFlag?: string
}) => {
  const t = useTranslations('pages.my_account.yearly_extension_banner')
  const language = useLocale()
  const featureEnabled = useFeatureIsOn(featureFlag ?? '')
  const { data: isEligibleForYearlyExtension } = useUserIsEligibleForAllAccessYearlyExtensionQuery()
  const { prices, currency } = usePricesForUser()

  const usps = [
    t('usp_1', {
      currency: currency.symbol,
      monthlyAmount: prices?.yearlyPromoMonthlySplit,
      yearlyAmount: prices?.yearlyPromo,
    }),
    t('usp_2', {
      currency: currency.symbol,
      monthlyAmount: prices?.yearlyPromoMonthlySplit,
      yearlyAmount: prices?.yearlyPromo,
    }),
    t('usp_3', {
      currency: currency.symbol,
      monthlyAmount: prices?.yearlyPromoMonthlySplit,
      yearlyAmount: prices?.yearlyPromo,
    }),
  ]

  React.useEffect(() => {
    if (isEligibleForYearlyExtension && (showBanner || featureEnabled)) {
      sendAmplitudeEvent(myAccountTrackingEvents.MY_ACCOUNT_EXTENSION_BANNER_SHOWN)
    }
  }, [isEligibleForYearlyExtension, showBanner, featureEnabled])

  // oxlint-disable-next-line require-await
  const handleUpgradeClick = React.useCallback(async () => {
    sendAmplitudeEvent(myAccountTrackingEvents.MY_ACCOUNT_EXTENSION_BANNER_CLICKED)
  }, [])

  if (!isEligibleForYearlyExtension || (!showBanner && !featureEnabled)) {
    return null
  }

  return (
    <Card className="!rounded-2 flex gap-6 [background:linear-gradient(180deg,_#F9F5EF_69.86%,_#FFF0DB_100%),_var(--CF-Grey-0,theme('colors.cf-white'))] md:flex-row lg:flex-nowrap lg:gap-10 lg:py-6">
      <div className="hidden self-center md:block">
        <FreeTrialIllustration className="h-40 w-40" />
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Typography variant="heading-4" className="text-cf-sf-gray-300">
            {t('sub_heading')}
          </Typography>
          <Typography variant="heading-2">
            {t('heading', {
              currency: currency.symbol,
              amount: prices?.yearlyPromoMonthlySplit,
            })}
          </Typography>
        </div>
        <ul className="flex flex-col gap-3">
          {usps.map(usp => (
            <li key={usp} className="flex items-center gap-3">
              <CheckCircleInvertedIcon className="size-6 shrink-0" />
              {usp}
            </li>
          ))}
        </ul>
        <span className="self-start">
          <Button
            variant="primary-green-bright"
            className="text-cf-white hover:text-cf-white hover:bg-secondary-700 my-2 h-[45px] self-start py-3 font-bold uppercase"
            asChild
          >
            <OneClickYearlyExtensionButton
              onClick={handleUpgradeClick}
              label={t('cta')}
              lang={language as Language}
              colors={{
                bgColor: '#3AB561',
                hoverBgColor: '#3AB561',
                textColor: '#fff',
                confirmBgColor: '#16A34A',
                confirmHoverBgColor: '#166534',
              }}
            />
          </Button>
        </span>
      </div>
    </Card>
  )
}
